import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2, Wrench, CheckCircle2, EyeOff, Bot, GitCommitHorizontal } from 'lucide-react'
import { ExceptionStatusBadge } from './status-badge'
import type { AppException } from '@/types/app-exceptions'

interface ExceptionDetailDialogProps {
  exception: AppException | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onResolve: (id: number) => Promise<void>
  onIgnore: (id: number) => Promise<void>
  onAnalyzeAndFix: (id: number) => Promise<void>
}

export function ExceptionDetailDialog({
  exception,
  open,
  onOpenChange,
  onResolve,
  onIgnore,
  onAnalyzeAndFix,
}: ExceptionDetailDialogProps) {
  const { t } = useTranslation('conversations')
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  if (!exception) return null

  const handleAction = async (action: string, fn: (id: number) => Promise<void>) => {
    setActionLoading(action)
    try {
      await fn(exception.id)
      onOpenChange(false)
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-full max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ExceptionStatusBadge status={exception.status} />
            {exception.severity && (
              <Badge variant={exception.severity === 'p0' ? 'destructive' : exception.severity === 'p1' ? 'default' : 'secondary'}>
                {exception.severity.toUpperCase()}
              </Badge>
            )}
            <Badge variant="outline">{t('errors.occurrences', { count: exception.occurrence_count })}</Badge>
          </div>
          <DialogTitle className="text-destructive text-lg">
            {exception.exception_class}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{exception.message}</p>
        </DialogHeader>

        <div className="space-y-4 min-w-0">
          {/* Location */}
          {exception.location && (
            <div>
              <h4 className="text-sm font-medium mb-1">{t('errors.detail.location')}</h4>
              <code className="text-xs bg-muted px-2 py-1 rounded">{exception.location}</code>
              {exception.function_name && (
                <span className="text-xs text-muted-foreground ml-2">
                  in <code className="bg-muted px-1 rounded">{exception.function_name}</code>
                </span>
              )}
            </div>
          )}

          {/* Request context */}
          {exception.request_context && exception.request_context.method && (
            <div>
              <h4 className="text-sm font-medium mb-1">{t('errors.detail.request')}</h4>
              <div className="space-y-1.5">
                <code className="block text-xs bg-muted px-2 py-1 rounded overflow-x-auto">
                  {exception.request_context.method} {exception.request_context.path}
                  {exception.request_context.query_string ? `?${exception.request_context.query_string}` : ''}
                </code>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  {exception.request_context.remote_addr && (
                    <div>IP: {exception.request_context.remote_addr}</div>
                  )}
                  {exception.request_context.user_agent && (
                    <div className="truncate">UA: {exception.request_context.user_agent}</div>
                  )}
                </div>
                {exception.request_context.headers && Object.keys(exception.request_context.headers).length > 0 && (
                  <pre className="text-xs bg-muted p-2 rounded max-h-32 overflow-auto">
                    {Object.entries(exception.request_context.headers).map(([k, v]) => `${k}: ${v}`).join('\n')}
                  </pre>
                )}
              </div>
            </div>
          )}

          {/* Celery context */}
          {exception.celery_context && exception.celery_context.task_name && (
            <div>
              <h4 className="text-sm font-medium mb-1">{t('errors.detail.celeryTask')}</h4>
              <code className="text-xs bg-muted px-2 py-1 rounded">{exception.celery_context.task_name}</code>
              {exception.celery_context.task_id && (
                <span className="text-xs text-muted-foreground ml-2">
                  ID: {exception.celery_context.task_id}
                </span>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="flex gap-6 text-xs text-muted-foreground">
            <div>
              <span className="font-medium">{t('errors.detail.firstSeen')}:</span>{' '}
              {new Date(exception.first_seen_at).toLocaleString()}
            </div>
            <div>
              <span className="font-medium">{t('errors.detail.lastSeen')}:</span>{' '}
              {new Date(exception.last_seen_at).toLocaleString()}
            </div>
          </div>

          {/* Resolution story */}
          {(exception.status === 'resolved' || exception.status === 'ignored') && (
            <div className="rounded-lg border bg-muted/50 p-3 space-y-1.5">
              <h4 className="text-sm font-medium flex items-center gap-1.5">
                {exception.status === 'resolved' && exception.resolved_by_pilot && (
                  <><Bot className="h-3.5 w-3.5" />{t('errors.resolution.resolvedByPilot')}</>
                )}
                {exception.status === 'resolved' && !exception.resolved_by_pilot && (
                  <><CheckCircle2 className="h-3.5 w-3.5" />{t('errors.resolution.resolvedManually')}</>
                )}
                {exception.status === 'ignored' && t('errors.status.ignored')}
              </h4>
              {exception.resolution_note && (
                <p className="text-sm text-muted-foreground">{exception.resolution_note}</p>
              )}
              {exception.resolution_commit_sha && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <GitCommitHorizontal className="h-3 w-3" />
                  {t('errors.resolution.fixedInCommit')}{' '}
                  <code className="bg-muted px-1.5 py-0.5 rounded font-mono">
                    {exception.resolution_commit_sha.slice(0, 7)}
                  </code>
                </div>
              )}
              {exception.close_reason && (
                <p className="text-sm text-muted-foreground">
                  {t('errors.resolution.closeReason')}: {exception.close_reason}
                </p>
              )}
              {exception.resolved_at && (
                <p className="text-xs text-muted-foreground">
                  {t('errors.resolution.resolvedAt')}: {new Date(exception.resolved_at).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Fix plan */}
          {exception.fix_plan && (
            <div>
              <h4 className="text-sm font-medium mb-1">{t('errors.diagnosis.fixPlan')}</h4>
              <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                {exception.fix_plan}
              </pre>
            </div>
          )}

          {/* Diagnosis status */}
          {exception.diagnosis_status && !exception.fix_plan && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {exception.diagnosis_status === 'diagnosis_pending' && t('errors.diagnosis.pending')}
              {exception.diagnosis_status === 'fix_failed' && t('errors.diagnosis.failed')}
            </div>
          )}

          {/* Traceback */}
          {exception.traceback && (
            <div>
              <h4 className="text-sm font-medium mb-1">{t('errors.detail.traceback')}</h4>
              <pre className="text-xs bg-zinc-900 text-zinc-100 p-3 rounded-lg overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
                {exception.traceback}
              </pre>
            </div>
          )}

          {/* Actions */}
          {exception.status === 'unresolved' && (
            <div className="flex gap-2 pt-2 border-t">
              <Button
                size="sm"
                onClick={() => handleAction('fix', onAnalyzeAndFix)}
                disabled={actionLoading !== null}
              >
                {actionLoading === 'fix' ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Wrench className="mr-2 h-3 w-3" />}
                {t('errors.actions.analyzeAndFix')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAction('resolve', onResolve)}
                disabled={actionLoading !== null}
              >
                {actionLoading === 'resolve' ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-2 h-3 w-3" />}
                {t('errors.actions.resolve')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleAction('ignore', onIgnore)}
                disabled={actionLoading !== null}
              >
                {actionLoading === 'ignore' ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <EyeOff className="mr-2 h-3 w-3" />}
                {t('errors.actions.ignore')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
